import React, { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * SearchSelect: a choice from a long list, found by typing. The options that
 * match what is typed (anywhere in the label or the detail line) show under
 * the box; one is picked by click or the keyboard (↑ ↓ Enter, Esc closes).
 * Looks and behaves like TownInput.
 *
 *   options     {Array<{value, label, detail?}>}
 *   value       single: the value chosen ('' for none); multiple: an array
 *   onChange    called with the new value (single) or array (multiple)
 *   multiple    {boolean} pick several: they show as chips above the box
 *   placeholder {string}
 *   invalid     {boolean} red edge, e.g. while a required one is empty
 *   id          {string}
 *   maxShown    {number}  how many matches to list at once
 */
const SearchSelect = ({ options, value, onChange, multiple = false, placeholder = 'Type to search', invalid = false, id, maxShown = 50 }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef(null);
  const chosen = multiple ? (Array.isArray(value) ? value : []) : value;
  const selected = multiple ? null : options.find((o) => String(o.value) === String(chosen));

  // Every typed word must appear; options already picked (multiple) drop out.
  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const picked = new Set(multiple ? chosen.map(String) : []);
    return options
      .filter((o) => !picked.has(String(o.value)))
      .filter((o) => {
        const hay = `${o.label} ${o.detail || ''}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .slice(0, maxShown);
  }, [options, query, multiple, chosen, maxShown]);

  const choose = (o) => {
    if (multiple) {
      onChange([...chosen, o.value]);
      setQuery('');
      setActive(0);
    } else {
      onChange(o.value);
      setQuery('');
      setOpen(false);
    }
  };

  // Single: shows the chosen name until the user starts typing.
  const shown = !multiple && !open && selected ? selected.label : query;

  return (
    <div className="relative">
      {multiple && chosen.length > 0 && (
        <ul className="mb-1.5 flex flex-wrap gap-1.5">
          {chosen.map((v) => {
            const o = options.find((x) => String(x.value) === String(v));
            return (
              <li key={v} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800/60">
                <span className="max-w-[14rem] truncate">{o ? o.label : v}</span>
                <button type="button" onClick={() => onChange(chosen.filter((c) => String(c) !== String(v)))}
                  className="p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50" aria-label={`Remove ${o ? o.label : v}`} title="Remove">
                  <X size={11} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="relative">
        <input
          id={id}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={shown}
          placeholder={!multiple && selected ? selected.label : placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => { setOpen(true); setActive(0); }}
          onBlur={() => { blurTimer.current = setTimeout(() => { setOpen(false); setQuery(''); }, 150); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); return; }
            if (!open || !matches.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
            if (e.key === 'Enter') { e.preventDefault(); choose(matches[active]); }
          }}
          className={`w-full ${!multiple && selected ? 'pr-8' : ''} ${invalid ? 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20' : ''}`}
        />
        {!multiple && selected && (
          <button type="button" onClick={() => { onChange(''); setQuery(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"
            aria-label="Clear" title="Clear">
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <ul role="listbox" className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-gray-500 dark:text-slate-400">{query ? 'No match' : 'Nothing left to choose'}</li>
          ) : matches.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); choose(o); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === active ? 'bg-blue-50 dark:bg-slate-600 text-blue-700 dark:text-blue-200' : 'text-gray-800 dark:text-slate-100'}`}
            >
              <div className="truncate">{o.label}</div>
              {o.detail && <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{o.detail}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchSelect;
